import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Save, Search } from "lucide-react";

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

export default function CrmManagerAssignments() {
    const [payload, setPayload] = useState(null);
    const [errored, setErrored] = useState(false);
    const [selectedManagerId, setSelectedManagerId] = useState("");
    const [selectedAgentIds, setSelectedAgentIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [mq, setMq] = useState("");
    const [aq, setAq] = useState("");

    const load = async () => {
        setErrored(false);
        try {
            const { data } = await api.get("/admin/crm/manager-assignments");
            setPayload(data && typeof data === "object" ? data : { managers: [], agents: [], assignments: {} });
            const managers = Array.isArray(data?.managers) ? data.managers : [];
            if (managers[0]) {
                setSelectedManagerId(managers[0].id);
            }
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!payload || !selectedManagerId) {
            setSelectedAgentIds([]);
            return;
        }
        const assigned = payload.assignments?.[selectedManagerId] || [];
        setSelectedAgentIds(Array.isArray(assigned) ? assigned : []);
    }, [selectedManagerId, payload]);

    const toggleAgent = (agentId) => {
        setSelectedAgentIds((prev) =>
            prev.includes(agentId) ? prev.filter((x) => x !== agentId) : [...prev, agentId]
        );
    };

    const save = async () => {
        if (!selectedManagerId) return;
        setSaving(true);
        try {
            await api.put(`/admin/crm/managers/${selectedManagerId}/agents`, { agent_ids: selectedAgentIds });
            toast.success("Manager assignments saved");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (errored) {
        return (
            <div className="p-6" data-testid="crm-manager-assignments-page">
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">Couldn't load manager assignments.</div>
            </div>
        );
    }

    if (!payload) {
        return (
            <div className="p-6" data-testid="crm-manager-assignments-page">
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
            </div>
        );
    }

    const managers = Array.isArray(payload.managers) ? payload.managers : [];
    const agents = Array.isArray(payload.agents) ? payload.agents : [];
    const selectedManager = managers.find((m) => m.id === selectedManagerId);
    const umatch = (u, term) => { const t = term.trim().toLowerCase(); return !t || [u.name, u.email, u.branch].some((v) => String(v || "").toLowerCase().includes(t)); };
    const shownManagers = managers.filter((m) => umatch(m, mq));
    const shownAgents = agents.filter((a) => umatch(a, aq));

    return (
        <div className="p-6 space-y-4" data-testid="crm-manager-assignments-page">
            <div>
                <div className="sc-section-title">Administration</div>
                <h1 className="text-2xl font-semibold tracking-tight">CRM Manager Assignments</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Assign which sales agents each manager can see in Team Activity and reports.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
                <div className="sc-card">
                    <div className="px-3 py-2 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">Managers</div>
                    <div className="p-2 border-b border-border">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input className="sc-input pl-8 h-8 text-sm" placeholder="Search managers…" value={mq} onChange={(e) => setMq(e.target.value)} data-testid="crm-manager-search" />
                        </div>
                    </div>
                    <ul className="max-h-[600px] overflow-y-auto">
                        {shownManagers.length === 0 && (
                            <li className="px-3 py-4 text-sm text-muted-foreground">No managers found.</li>
                        )}
                        {shownManagers.map((m) => (
                            <li key={m.id}>
                                <button
                                    onClick={() => setSelectedManagerId(m.id)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary ${selectedManagerId === m.id ? "bg-secondary" : ""}`}
                                    data-testid={`crm-manager-pick-${m.id}`}
                                >
                                    <div className="font-medium">{s(m.name, m.email)}</div>
                                    <div className="text-xs text-muted-foreground font-mono">{s(m.email)}</div>
                                    <div className="text-xs text-muted-foreground">{s(m.branch)}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="sc-card">
                    {!selectedManager ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">Select a manager to manage their team.</div>
                    ) : (
                        <div>
                            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                                <div>
                                    <div className="font-semibold">{s(selectedManager.name, selectedManager.email)}</div>
                                    <div className="text-xs text-muted-foreground font-mono">{s(selectedManager.email)}</div>
                                </div>
                                <button
                                    onClick={save}
                                    disabled={saving}
                                    className="h-9 inline-flex items-center gap-1 px-3 rounded-md bg-foreground text-background text-sm font-medium disabled:opacity-50"
                                    data-testid="crm-manager-assignments-save"
                                >
                                    <Save className="w-4 h-4" /> Save Team
                                </button>
                            </div>

                            <div className="p-4">
                                <div className="relative max-w-sm mb-3">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <input className="sc-input pl-9" placeholder="Search agents…" value={aq} onChange={(e) => setAq(e.target.value)} data-testid="crm-agent-search" />
                                </div>
                                {agents.length === 0 && (
                                    <div className="text-sm text-muted-foreground">No sales agents available.</div>
                                )}
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {shownAgents.map((a) => (
                                        <label key={a.id} className="flex items-start gap-2 text-sm cursor-pointer border border-border rounded-md p-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedAgentIds.includes(a.id)}
                                                onChange={() => toggleAgent(a.id)}
                                                className="mt-1"
                                                data-testid={`crm-manager-agent-toggle-${a.id}`}
                                            />
                                            <span>
                                                <span className="font-medium block">{s(a.name, a.email)}</span>
                                                <span className="block text-xs text-muted-foreground font-mono">{s(a.email)}</span>
                                                <span className="block text-xs text-muted-foreground">{s(a.branch)}</span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
